import { useToast } from "@/hooks/use-toast"
import { useMembersList } from "./useMembersList"
import { useMemberDialogs } from "./useMemberDialogs"
import { useMemberForms } from "./useMemberForms"
import { useCurrentMemberState } from "./useCurrentMemberState"
import { useMemberAddition } from "./useMemberAddition"
import { useMemberEditing } from "./useMemberEditing"
import { useMemberRemoval } from "./useMemberRemoval"
import { useOwnershipTransfer } from "./useOwnershipTransfer"
import {
  countCheckedPermissions,
  getCheckedPermissionNames,
} from "./memberUtils"
import { Member, MemberRole } from "../../types/memberTypes"

export const useMembersState = (
  source: "portfolio" | "business" = "portfolio"
) => {
  const { members, setMembers, entityId } = useMembersList(source)
  const dialogState = useMemberDialogs()
  const formState = useMemberForms()

  // Use our refactored hooks
  const {
    currentMember,
    isCurrentMemberPrimaryOwner,
    isCurrentMemberOwner,
    canBusinessMembersBeManaged,
    effectivePermissionForActions,
    hasManageMembersPermission,
    hasBusinessMembersPermission,
  } = useCurrentMemberState(members, setMembers, source)

  const { handleAddMember, handleAddMemberSubmit } = useMemberAddition(
    setMembers,
    dialogState.setShowAddMemberDialog,
    formState.resetInviteMemberForm,
    entityId,
    source
  )

  const {
    editingMemberId,
    setEditingMemberId, // Make sure we have this from the hook
    handleEditClick,
    handleCancelEdit,
    handleSaveEdit,
    handleRoleChange: baseHandleRoleChange,
    handlePermissionChange,
  } = useMemberEditing(setMembers, effectivePermissionForActions, entityId)

  const {
    handleRemoveMemberClick: baseHandleRemoveMemberClick,
    handleRemoveCurrentUser: baseHandleRemoveCurrentUser,
    handleConfirmDelete: baseHandleConfirmDelete,
    handleLeavePortfolio,
    handleResendInvite,
  } = useMemberRemoval(setMembers, source, entityId)

  const { handleConfirmOwnershipTransfer: baseHandleConfirmOwnershipTransfer } =
    useOwnershipTransfer(setMembers, setEditingMemberId)

  // Wrapper functions that provide the necessary dependencies
  const handleRoleChange = (memberId: string, role: MemberRole) => {
    baseHandleRoleChange(
      memberId,
      role,
      dialogState.setMemberToPromote,
      dialogState.setShowTransferOwnershipDialog,
      members
    )
  }

  const handleRemoveMemberClick = (member: Member) => {
    baseHandleRemoveMemberClick(
      member,
      dialogState.setMemberToDelete,
      dialogState.setShowDeleteMemberDialog,
      dialogState.setShowPrimaryOwnerDialog
    )
  }

  const handleRemoveCurrentUser = () => {
    baseHandleRemoveCurrentUser(
      members,
      dialogState.setShowPrimaryOwnerDialog,
      dialogState.setShowLeavePortfolioDialog
    )
  }

  const handleConfirmDelete = () => {
    baseHandleConfirmDelete(
      dialogState.memberToDelete,
      dialogState.setMemberToDelete,
      dialogState.setShowDeleteMemberDialog
    )
  }

  const handleConfirmOwnershipTransfer = () => {
    baseHandleConfirmOwnershipTransfer(
      dialogState.memberToPromote,
      dialogState.setMemberToPromote,
      dialogState.setShowTransferOwnershipDialog
    )
  }

  // Add additional debugging to understand why the Add Member button isn't showing
  console.log(
    "useMembersState - Can add members:",
    Boolean(
      isCurrentMemberOwner ||
        isCurrentMemberPrimaryOwner ||
        hasManageMembersPermission
    )
  )
  console.log(
    "useMembersState - handleAddMember exists:",
    Boolean(handleAddMember)
  )

  return {
    // Member data
    members,
    editingMemberId,
    inviteMemberForm: formState.inviteMemberForm,

    // Dialog states
    ...dialogState,

    // Member permissions
    isCurrentMemberPrimaryOwner,
    isCurrentMemberOwner,
    canManageBusinessMembers: canBusinessMembersBeManaged,
    hasBusinessMembersPermission,

    // Helper functions
    getCheckedPermissionsCount: countCheckedPermissions,
    getCheckedPermissionNames,

    // Member management actions
    handleAddMember,
    handleAddMemberSubmit,
    handleRemoveMemberClick: effectivePermissionForActions
      ? handleRemoveMemberClick
      : undefined,
    handleEditClick: effectivePermissionForActions
      ? handleEditClick
      : undefined,
    handleCancelEdit,
    handleSaveEdit,
    handleRoleChange,
    handlePermissionChange,
    handleConfirmDelete,
    handleConfirmOwnershipTransfer,
    handleRemoveCurrentUser,
    handleLeavePortfolio,
    handleResendInvite,
  }
}
